// ============================================================
// Ebek Sales CRM — Automation Rules Builder
// Visual IF/THEN rule editor for managers and admins
// ============================================================
import React, { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import API from "../../utils/api";

// ── Field options for conditions ─────────────────────────────
const CONDITION_FIELDS = [
  { key: "pipeline_stage",     label: "Pipeline Stage",        type: "stage" },
  { key: "lead_status",        label: "Lead Temperature",       type: "temperature" },
  { key: "days_since_stage",   label: "Days in Current Stage",  type: "number" },
  { key: "days_since_visit",   label: "Days Since Last Visit",  type: "number" },
  { key: "days_since_followup",label: "Days Since Last Follow-up", type: "number" },
];

const STAGES = [
  "visited","interested","proposal_sent","mou_shared","mou_signed","payment_requested",
];

const STAGE_LABELS = {
  visited: "Visited", interested: "Interested", proposal_sent: "Proposal Sent",
  mou_shared: "MOU Shared", mou_signed: "MOU Signed", payment_requested: "Payment Requested",
};

const OPERATORS_NUM = [
  { key: "gt",  label: "more than" },
  { key: "gte", label: "at least" },
  { key: "lt",  label: "less than" },
  { key: "eq",  label: "exactly" },
];

const OPERATORS_EQ = [
  { key: "eq",  label: "is" },
  { key: "neq", label: "is not" },
];

// ── Action options ────────────────────────────────────────────
const ACTION_TYPES = [
  { key: "send_whatsapp",   label: "💬 Send WhatsApp" },
  { key: "set_temperature", label: "🌡 Change Temperature" },
  { key: "create_followup", label: "📅 Create Follow-up" },
];

const WA_TEMPLATES = [
  { key: "follow_up",       label: "General Follow-up" },
  { key: "going_cold",      label: "Re-engagement (Going Cold)" },
  { key: "proposal_nudge",  label: "Proposal Nudge" },
  { key: "mou_reminder",    label: "MOU Reminder" },
  { key: "payment_reminder",label: "Payment Reminder" },
];

// ── Pre-built rule templates ──────────────────────────────────
const PRESET_RULES = [
  {
    name: "Stage Stuck — Visited",
    description: "Lead has been in Visited stage for more than 10 days without a follow-up",
    conditions: [
      { field: "pipeline_stage",     op: "eq",  value: "visited" },
      { field: "days_since_followup",op: "gt",  value: 10 },
    ],
    actions: [
      { type: "send_whatsapp", template: "follow_up" },
    ],
    cooldown_days: 5,
  },
  {
    name: "Going Cold — Hot Lead",
    description: "Hot lead hasn't been visited in 14+ days",
    conditions: [
      { field: "lead_status",      op: "eq",  value: "hot" },
      { field: "days_since_visit", op: "gt",  value: 14 },
    ],
    actions: [
      { type: "send_whatsapp", template: "going_cold" },
      { type: "set_temperature", value: "warm" },
    ],
    cooldown_days: 7,
  },
  {
    name: "Proposal Pending",
    description: "Lead has been in Proposal Sent stage for more than 7 days",
    conditions: [
      { field: "pipeline_stage",   op: "eq",  value: "proposal_sent" },
      { field: "days_since_stage", op: "gt",  value: 7 },
    ],
    actions: [
      { type: "send_whatsapp", template: "proposal_nudge" },
    ],
    cooldown_days: 4,
  },
  {
    name: "MOU Awaiting Signature",
    description: "MOU shared but not signed for 5+ days",
    conditions: [
      { field: "pipeline_stage",   op: "eq",  value: "mou_shared" },
      { field: "days_since_stage", op: "gt",  value: 5 },
    ],
    actions: [
      { type: "send_whatsapp", template: "mou_reminder" },
    ],
    cooldown_days: 3,
  },
  {
    name: "Payment Overdue",
    description: "Payment requested but not received for 5+ days",
    conditions: [
      { field: "pipeline_stage",   op: "eq",  value: "payment_requested" },
      { field: "days_since_stage", op: "gt",  value: 5 },
    ],
    actions: [
      { type: "send_whatsapp", template: "payment_reminder" },
    ],
    cooldown_days: 3,
  },
];

function newCondition() {
  return { field: "pipeline_stage", op: "eq", value: "visited" };
}

function newAction() {
  return { type: "send_whatsapp", template: "follow_up" };
}

export default function AutomationRules({ accent }) {
  const [rules, setRules]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState("list"); // "list" | "builder" | "logs"
  const [editing, setEditing] = useState(null);  // null | rule object
  const [logsRule, setLogsRule] = useState(null);
  const [logs, setLogs]       = useState([]);
  const [running, setRunning] = useState(false);

  // Builder state
  const [bName, setBName]             = useState("");
  const [bDesc, setBDesc]             = useState("");
  const [bConditions, setBConditions] = useState([newCondition()]);
  const [bActions, setBActions]       = useState([newAction()]);
  const [bCooldown, setBCooldown]     = useState(3);
  const [saving, setSaving]           = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get("/automation");
      setRules(r.data);
    } catch {
      toast.error("Failed to load rules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  function startCreate() {
    setEditing(null);
    setBName(""); setBDesc(""); setBCooldown(3);
    setBConditions([newCondition()]);
    setBActions([newAction()]);
    setView("builder");
  }

  function startEdit(rule) {
    setEditing(rule);
    setBName(rule.name);
    setBDesc(rule.description || "");
    setBCooldown(rule.cooldown_days || 3);
    setBConditions(rule.conditions?.length ? rule.conditions : [newCondition()]);
    setBActions(rule.actions?.length ? rule.actions : [newAction()]);
    setView("builder");
  }

  function applyPreset(preset) {
    setBName(preset.name);
    setBDesc(preset.description);
    setBCooldown(preset.cooldown_days);
    setBConditions(preset.conditions);
    setBActions(preset.actions);
  }

  async function handleSave() {
    if (!bName.trim()) return toast.error("Rule name is required");
    if (bConditions.length === 0) return toast.error("Add at least one condition");
    if (bActions.length === 0) return toast.error("Add at least one action");

    setSaving(true);
    try {
      const payload = {
        name:         bName.trim(),
        description:  bDesc,
        conditions:   bConditions,
        actions:      bActions,
        cooldown_days: bCooldown,
      };
      if (editing) {
        await API.put(`/automation/${editing.id}`, payload);
        toast.success("Rule updated ✓");
      } else {
        await API.post("/automation", payload);
        toast.success("Rule created ✓");
      }
      await fetchRules();
      setView("list");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  }

  async function toggleRule(rule) {
    try {
      await API.put(`/automation/${rule.id}`, { is_active: !rule.is_active });
      setRules(rs => rs.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r));
    } catch {
      toast.error("Failed to update rule");
    }
  }

  async function deleteRule(rule) {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await API.delete(`/automation/${rule.id}`);
      setRules(rs => rs.filter(r => r.id !== rule.id));
      toast.success("Rule deleted");
    } catch {
      toast.error("Failed to delete");
    }
  }

  async function runNow() {
    setRunning(true);
    try {
      const r = await API.post("/automation/run-now");
      toast.success(`Automation ran — ${r.data.actions_fired} action(s) fired`);
    } catch {
      toast.error("Run failed");
    } finally {
      setRunning(false);
    }
  }

  async function viewLogs(rule) {
    setLogsRule(rule);
    setView("logs");
    try {
      const r = await API.get(`/automation/${rule.id}/logs`);
      setLogs(r.data);
    } catch {
      toast.error("Failed to load logs");
    }
  }

  // ── Condition helpers ─────────────────────────────────────
  function updateCondition(i, key, val) {
    setBConditions(prev => prev.map((c, idx) => idx === i ? { ...c, [key]: val } : c));
  }
  function removeCondition(i) {
    setBConditions(prev => prev.filter((_, idx) => idx !== i));
  }

  // ── Action helpers ────────────────────────────────────────
  function updateAction(i, key, val) {
    setBActions(prev => prev.map((a, idx) => idx === i ? { ...a, [key]: val } : a));
  }
  function removeAction(i) {
    setBActions(prev => prev.filter((_, idx) => idx !== i));
  }

  const sectionTitle = { fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", marginBottom: 8 };
  const card = { background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 14, padding: 16 };

  // ── LOGS VIEW ─────────────────────────────────────────────
  if (view === "logs") {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <button onClick={() => setView("list")}
            style={{ background: "#f1f5f9", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 12, color: "#64748b" }}>
            ← Back
          </button>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}>Rule Execution Logs</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{logsRule?.name}</div>
          </div>
        </div>
        {logs.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
            <div style={{ fontWeight: 600 }}>No executions yet</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {logs.map(log => (
              <div key={log.id} style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{log.institution_name}</div>
                  <div style={{ fontSize: 10, color: "#94a3b8" }}>{new Date(log.fired_at).toLocaleString("en-IN")}</div>
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>Rep: {log.rep_name}</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(log.actions_taken || []).map((a, i) => (
                    <span key={i} style={{ background: a.result === "sent" || a.result === "done" || a.result === "created" ? "#dcfce7" : "#fee2e2", color: a.result === "sent" || a.result === "done" || a.result === "created" ? "#15803d" : "#dc2626", borderRadius: 6, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>
                      {a.type}: {a.result}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── BUILDER VIEW ──────────────────────────────────────────
  if (view === "builder") {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <button onClick={() => setView("list")}
            style={{ background: "#f1f5f9", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 12, color: "#64748b" }}>
            ← Back
          </button>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#1e293b" }}>
            {editing ? "Edit Rule" : "Create Automation Rule"}
          </div>
        </div>

        {/* Quick presets (only for new rules) */}
        {!editing && (
          <div style={{ marginBottom: 20 }}>
            <div style={sectionTitle}>⚡ Start from a preset</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {PRESET_RULES.map(p => (
                <button key={p.name} onClick={() => applyPreset(p)}
                  style={{ padding: "7px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 700, color: "#475569" }}>
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Name + description */}
        <div style={{ marginBottom: 16 }}>
          <div style={sectionTitle}>Rule Name</div>
          <input value={bName} onChange={e => setBName(e.target.value)}
            placeholder="e.g. Re-engage cold leads after 14 days"
            style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", marginBottom: 8, boxSizing: "border-box" }} />
          <textarea value={bDesc} onChange={e => setBDesc(e.target.value)}
            placeholder="Optional description"
            rows={2}
            style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
        </div>

        {/* CONDITIONS */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={sectionTitle}>IF (Conditions — ALL must match)</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {bConditions.map((cond, i) => {
              const fieldCfg = CONDITION_FIELDS.find(f => f.key === cond.field) || CONDITION_FIELDS[0];
              return (
                <div key={i} style={{ background: "#f0f9ff", border: "1.5px solid #bae6fd", borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <select value={cond.field} onChange={e => updateCondition(i, "field", e.target.value)}
                      style={{ padding: "7px 10px", borderRadius: 8, border: "1.5px solid #bae6fd", fontFamily: "inherit", fontSize: 12, fontWeight: 700, background: "#fff", outline: "none" }}>
                      {CONDITION_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>

                    <select value={cond.op} onChange={e => updateCondition(i, "op", e.target.value)}
                      style={{ padding: "7px 10px", borderRadius: 8, border: "1.5px solid #bae6fd", fontFamily: "inherit", fontSize: 12, fontWeight: 700, background: "#fff", outline: "none" }}>
                      {(fieldCfg.type === "number"
                        ? OPERATORS_NUM
                        : OPERATORS_EQ
                      ).map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>

                    {fieldCfg.type === "stage" ? (
                      <select value={cond.value} onChange={e => updateCondition(i, "value", e.target.value)}
                        style={{ padding: "7px 10px", borderRadius: 8, border: "1.5px solid #bae6fd", fontFamily: "inherit", fontSize: 12, fontWeight: 700, background: "#fff", outline: "none" }}>
                        {STAGES.map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                      </select>
                    ) : fieldCfg.type === "temperature" ? (
                      <select value={cond.value} onChange={e => updateCondition(i, "value", e.target.value)}
                        style={{ padding: "7px 10px", borderRadius: 8, border: "1.5px solid #bae6fd", fontFamily: "inherit", fontSize: 12, fontWeight: 700, background: "#fff", outline: "none" }}>
                        <option value="hot">🔥 Hot</option>
                        <option value="warm">🌤 Warm</option>
                        <option value="cold">❄️ Cold</option>
                      </select>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <input type="number" value={cond.value} onChange={e => updateCondition(i, "value", e.target.value)}
                          min={0} max={365}
                          style={{ width: 60, padding: "7px 10px", borderRadius: 8, border: "1.5px solid #bae6fd", fontFamily: "inherit", fontSize: 12, fontWeight: 700, background: "#fff", outline: "none", textAlign: "center" }} />
                        <span style={{ fontSize: 11, color: "#0284c7" }}>days</span>
                      </div>
                    )}

                    {bConditions.length > 1 && (
                      <button onClick={() => removeCondition(i)}
                        style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18, padding: 4 }}>✕</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={() => setBConditions(prev => [...prev, newCondition()])}
            style={{ marginTop: 8, padding: "7px 14px", borderRadius: 8, border: "1.5px dashed #bae6fd", background: "#f0f9ff", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 700, color: "#0284c7" }}>
            + Add Condition
          </button>
        </div>

        {/* ACTIONS */}
        <div style={{ marginBottom: 20 }}>
          <div style={sectionTitle}>THEN (Actions)</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {bActions.map((action, i) => (
              <div key={i} style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 12, padding: "12px 14px" }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <select value={action.type} onChange={e => updateAction(i, "type", e.target.value)}
                    style={{ padding: "7px 10px", borderRadius: 8, border: "1.5px solid #86efac", fontFamily: "inherit", fontSize: 12, fontWeight: 700, background: "#fff", outline: "none" }}>
                    {ACTION_TYPES.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                  </select>

                  {action.type === "send_whatsapp" && (
                    <select value={action.template || "follow_up"} onChange={e => updateAction(i, "template", e.target.value)}
                      style={{ padding: "7px 10px", borderRadius: 8, border: "1.5px solid #86efac", fontFamily: "inherit", fontSize: 12, fontWeight: 700, background: "#fff", outline: "none" }}>
                      {WA_TEMPLATES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                  )}

                  {action.type === "set_temperature" && (
                    <select value={action.value || "warm"} onChange={e => updateAction(i, "value", e.target.value)}
                      style={{ padding: "7px 10px", borderRadius: 8, border: "1.5px solid #86efac", fontFamily: "inherit", fontSize: 12, fontWeight: 700, background: "#fff", outline: "none" }}>
                      <option value="hot">🔥 Hot</option>
                      <option value="warm">🌤 Warm</option>
                      <option value="cold">❄️ Cold</option>
                    </select>
                  )}

                  {action.type === "create_followup" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 11, color: "#15803d" }}>in</span>
                      <input type="number" value={action.days_from_now || 1} min={1} max={30}
                        onChange={e => updateAction(i, "days_from_now", e.target.value)}
                        style={{ width: 50, padding: "7px 8px", borderRadius: 8, border: "1.5px solid #86efac", fontFamily: "inherit", fontSize: 12, fontWeight: 700, background: "#fff", outline: "none", textAlign: "center" }} />
                      <span style={{ fontSize: 11, color: "#15803d" }}>days</span>
                    </div>
                  )}

                  {bActions.length > 1 && (
                    <button onClick={() => removeAction(i)}
                      style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18, padding: 4 }}>✕</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => setBActions(prev => [...prev, newAction()])}
            style={{ marginTop: 8, padding: "7px 14px", borderRadius: 8, border: "1.5px dashed #86efac", background: "#f0fdf4", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 700, color: "#15803d" }}>
            + Add Action
          </button>
        </div>

        {/* Cooldown */}
        <div style={{ marginBottom: 24 }}>
          <div style={sectionTitle}>Cooldown — Don't fire again on the same lead for</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input type="number" value={bCooldown} min={1} max={30} onChange={e => setBCooldown(parseInt(e.target.value) || 1)}
              style={{ width: 64, padding: "10px 12px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 14, fontFamily: "inherit", fontWeight: 700, outline: "none", textAlign: "center" }} />
            <span style={{ fontSize: 13, color: "#64748b" }}>days</span>
          </div>
        </div>

        <button onClick={handleSave} disabled={saving}
          style={{ width: "100%", padding: 14, background: saving ? "#e2e8f0" : accent, color: saving ? "#94a3b8" : "#fff", border: "none", borderRadius: 14, fontSize: 15, fontWeight: 800, cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
          {saving ? "Saving…" : editing ? "Update Rule" : "Create Rule"}
        </button>
      </div>
    );
  }

  // ── LIST VIEW ─────────────────────────────────────────────
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b" }}>⚡ Automation Rules</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>IF-THEN rules that run automatically each morning</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={runNow} disabled={running}
            style={{ padding: "8px 12px", borderRadius: 10, border: `1.5px solid ${accent}`, background: "#fff", color: accent, fontFamily: "inherit", fontWeight: 700, fontSize: 11, cursor: running ? "not-allowed" : "pointer" }}>
            {running ? "Running…" : "▶ Run Now"}
          </button>
          <button onClick={startCreate}
            style={{ padding: "8px 14px", borderRadius: 10, border: "none", background: accent, color: "#fff", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            + New Rule
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", color: "#94a3b8", padding: 40 }}>Loading…</div>
      ) : rules.length === 0 ? (
        <div style={{ textAlign: "center", padding: 48, color: "#94a3b8" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚡</div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>No automation rules yet</div>
          <div style={{ fontSize: 13, marginBottom: 20 }}>Create rules to automatically follow up on stuck leads, send WhatsApp reminders, and more</div>
          <button onClick={startCreate}
            style={{ padding: "11px 22px", borderRadius: 12, border: "none", background: accent, color: "#fff", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            Create First Rule
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rules.map(rule => (
            <div key={rule.id} style={{ background: "#fff", border: `1.5px solid ${rule.is_active ? "#e2e8f0" : "#f1f5f9"}`, borderRadius: 14, padding: 16, opacity: rule.is_active ? 1 : 0.65 }}>
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", marginBottom: 2 }}>{rule.name}</div>
                  {rule.description && <div style={{ fontSize: 12, color: "#64748b" }}>{rule.description}</div>}
                </div>
                {/* Active toggle */}
                <button onClick={() => toggleRule(rule)}
                  style={{ width: 42, height: 24, borderRadius: 100, border: "none", cursor: "pointer", padding: 3, background: rule.is_active ? "#15803d" : "#cbd5e1", transition: "background 0.2s", display: "flex", alignItems: "center", flexShrink: 0, marginLeft: 12 }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", transform: rule.is_active ? "translateX(18px)" : "translateX(0)", transition: "transform 0.2s" }} />
                </button>
              </div>

              {/* Conditions summary */}
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", marginBottom: 4 }}>IF</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {(rule.conditions || []).map((c, i) => (
                    <span key={i} style={{ background: "#f0f9ff", color: "#0284c7", borderRadius: 6, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>
                      {CONDITION_FIELDS.find(f => f.key === c.field)?.label || c.field} {c.op} {c.value}
                    </span>
                  ))}
                </div>
              </div>

              {/* Actions summary */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", marginBottom: 4 }}>THEN</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {(rule.actions || []).map((a, i) => (
                    <span key={i} style={{ background: "#f0fdf4", color: "#15803d", borderRadius: 6, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>
                      {ACTION_TYPES.find(t => t.key === a.type)?.label || a.type}
                    </span>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8, borderTop: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: 10, color: "#94a3b8" }}>
                  Cooldown: {rule.cooldown_days}d · Fired: {rule.run_count} times
                  {rule.last_run_at && ` · Last: ${new Date(rule.last_run_at).toLocaleDateString("en-IN")}`}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => viewLogs(rule)}
                    style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 10, fontWeight: 700, color: "#64748b" }}>
                    Logs
                  </button>
                  <button onClick={() => startEdit(rule)}
                    style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${accent}`, background: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 10, fontWeight: 700, color: accent }}>
                    Edit
                  </button>
                  <button onClick={() => deleteRule(rule)}
                    style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid #fecaca", background: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 10, fontWeight: 700, color: "#dc2626" }}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
